import { Children, useMemo } from 'react'

import clsx from 'clsx'

import { AppIcon } from 'shared/ui/app-icon'
import { AllIcons } from 'shared/ui/app-icon/types'

import { toCssSize } from './lib/to-css-size'
import { useSlider } from './lib/use-slider'
import s from './styles.module.scss'
import { type ScrollSnapSliderProps, type SliderStyleVars } from './types'

export const ScrollSnapSlider = ({
  children,
  className,
  trackClassName,
  slideClassName,
  showArrows = true,
  strictSwipeStep = false,
  dragMode = 'step',
  activeIndex,
  defaultIndex = 0,
  onChange,
  slideWidth = '100%',
  gap = 0,
}: ScrollSnapSliderProps) => {
  const slides = useMemo(() => Children.toArray(children), [children])

  const {
    trackRef,
    canGoPrev,
    canGoNext,
    goPrev,
    goNext,
    isDragging,
    onScroll,
    onTouchStart,
    onTouchEnd,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel,
    onClickCapture,
  } = useSlider({
    slideCount: slides.length,
    strictSwipeStep,
    dragMode,
    activeIndex,
    defaultIndex,
    onChange,
  })

  const sliderStyle = useMemo<SliderStyleVars>(
    () => ({
      '--scroll-snap-slider-gap': toCssSize(gap),
      '--scroll-snap-slider-slide-width': toCssSize(slideWidth),
    }),
    [gap, slideWidth],
  )

  return (
    <div className={clsx(s.root, className)}>
      {showArrows && (
        <>
          <button
            type="button"
            onClick={goPrev}
            disabled={!canGoPrev}
            className={clsx(s.arrow, s.arrowLeft)}
          >
            <AppIcon
              id={AllIcons.ARROW_LEFT}
              width={20}
              height={20}
            />
          </button>

          <button
            type="button"
            onClick={goNext}
            disabled={!canGoNext}
            className={clsx(s.arrow, s.arrowRight)}
          >
            <AppIcon
              id={AllIcons.ARROW_RIGHT}
              width={20}
              height={20}
            />
          </button>
        </>
      )}

      <div
        ref={trackRef}
        onScroll={onScroll}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
        onClickCapture={onClickCapture}
        className={clsx(
          s.track,
          dragMode === 'step' && strictSwipeStep && s.strictSwipeTrack,
          dragMode === 'free' && s.freeDragTrack,
          dragMode === 'free' && isDragging && s.draggingTrack,
          trackClassName,
        )}
        style={sliderStyle}
      >
        {slides.map((slide, index) => (
          <div
            className={clsx(s.slide, slideClassName)}
            key={index}
          >
            {slide}
          </div>
        ))}
      </div>
    </div>
  )
}
